import React, { useEffect, useMemo, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { createRole, deleteRole, fetchAllRoles, updateRole, } from "../../../core/data/redux/user/role/roleAction";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../core/common/GlobalFunctions/exportTableData";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { Button, Drawer, Form, Input, message, Select } from "antd";
import { status } from "../../../core/common/selectoption/selectoption";
import { formatDate } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";

const RolesPermission = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedDataStatus, setSelectedDataStatus] = useState("active");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<any>("");
  const [roleName, setRoleName] = useState<string>("");
  const { user } = useSelector((state: any) => state.auth);
  const { roles, loadingStates } = useSelector((state: any) => state.role);
  const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;
  const roleId = selectedRowData?.RoleId;

  useEffect(() => {
    dispatch(fetchAllRoles());
  }, [dispatch]);

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];


  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");
  const canImport =
    permissions.includes("Import") || permissions.includes("AllowAll");


  useEffect(() => {
    if (modalType === "edit" && roleId) {
      setRoleName(selectedRowData?.RoleName);
      setSelectedStatus(selectedRowData?.Status);
    }
  }, [selectedRowData]);

  const resetFields = () => {
    setRoleName("");
    setSelectedStatus("");
    setSelectedRowData(null);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const showDrawer = (type: "edit" | "create") => {
    setModalType(type);
    setIsDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerVisible(false);
    setModalType("");
    resetFields();
  };

  const handleEditDepartment = (record: any): void => {
    setSelectedRowData(record);
  };

  const handleSubmit = async () => {
    if (!roleName) {
      message.warning("Role Name required");
      return;
    }
    if (!selectedStatus) {
      message.warning("Status required");
      return;
    }
    const RoleData = {
      RoleName: roleName,
      Status: selectedStatus,
      CreatedBy: user?.UserId,
      ModifiedBy: user?.UserId,
    };

    if (modalType === "create") {
      const result = await dispatch(createRole(RoleData));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Role created successfully");
        resetFields();
        await dispatch(fetchAllRoles());
      } else if (result.meta.requestStatus === "rejected") {
        message.error(result.payload.error);
      }
    } else if (modalType === "edit" && roleId) {
      const result = await dispatch(updateRole({ roleId, data: RoleData }));
      if (result.meta.requestStatus === "fulfilled") {
        message.success("Role updated successfully");
        resetFields();
        await dispatch(fetchAllRoles());
      } else if (result.meta.requestStatus === "rejected") {
        message.error(result.payload.error);
      }
    }
    handleCloseDrawer();
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      const result = await dispatch(deleteRole(roleId));
      if (result.meta.requestStatus === "fulfilled") {
        message.success(result.payload.message);
        setIsDeleteModalVisible(false);
        await dispatch(fetchAllRoles());
      } else {
        message.warning(result.payload.message);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const data = roles ? roles : [];

  function filterRoleData(data: any[]) {
  return data.map(role => ({
    roleName: role.RoleName || "N/A",
    status: role.Status || "N/A",
    createdAt: role.CreatedAt ? new Date(role.CreatedAt).toLocaleDateString() : "N/A",
    modifiedAt: role.ModifiedAt ? new Date(role.ModifiedAt).toLocaleDateString() : "N/A",
  }));
}

const dataforexport = filterRoleData(data)


  const filteredData = useMemo(() => {
    const filters =
      selectedDataStatus && selectedDataStatus !== "All"
        ? { Status: selectedDataStatus }
        : {};
    return filterData(
      data || [],
      filters,
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [roles, selectedDataStatus, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedDataStatus, selectedSort, roles]);

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Role",
      dataIndex: "RoleName",
      sorter: (a: any, b: any) => a.RoleName.length - b.RoleName.length,
    },
    {
      title: "Created Date",
      dataIndex: "CreatedAt",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.CreatedAt.length - b.CreatedAt.length,
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => (
        <span
          className={`badge ${text === "active" ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

        </span>
      ),
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to={
              canView
                ? `${all_routes.permissionpage.replace(
                  ":roleId",
                  record.RoleId
                )}`
                : "#"
            }
            className="me-2"
            onClick={(e) => {
              if (!canView) e.preventDefault();
            }}
            style={{
              pointerEvents: canView ? "auto" : "none",
              opacity: canView ? 1 : 0.5,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="permission"
          >
            <i className="ti ti-shield" />
          </Link>

          <button
            className="me-2"
            onClick={() => {
              if (!canEdit) return;
              showDrawer("edit");
              handleEditDepartment(record);
            }}
            disabled={!canEdit}
            style={{
              cursor: canEdit ? "pointer" : "not-allowed",
              opacity: canEdit ? 1 : 0.5,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Edit"
          >
            <i className="ti ti-edit" />
          </button>

          {/* Delete Button (disabled if no delete permission or status is inactive) */}
          <button
            onClick={() => {
              if (!canDelete || record.Status === "inactive") return;
              setSelectedRowData(record);
              setIsDeleteModalVisible(true);
            }}
            className="me-2"
            disabled={!canDelete || record.Status === "inactive"}
            style={{
              cursor:
                !canDelete || record.Status === "inactive"
                  ? "not-allowed"
                  : "pointer",
              opacity: !canDelete || record.Status === "inactive" ? 0.5 : 1,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Delete"
          >
            <i className="ti ti-trash" />
          </button>

          {/* <button
            onClick={() => {
              if (!canAdd) return;
              showDrawer("create"); // <-- your add handler
            }}
            className="me-2"
            disabled={!canAdd}
            style={{
              cursor: canAdd ? "pointer" : "not-allowed",
              opacity: canAdd ? 1 : 0.5,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Add"
          >
            <i className="ti ti-plus" />
          </button>

          <button
            className="me-2"
            disabled={!canExport}
            style={{
              cursor: canExport ? "pointer" : "not-allowed",
              opacity: canExport ? 1 : 0.5,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Export"
          >
            <i className="ti ti-download" />
          </button>


          <button
            className="me-2"
            disabled={!canImport}
            style={{
              cursor: canImport ? "pointer" : "not-allowed",
              opacity: canImport ? 1 : 0.5,
            }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Import"
          >
            <i className="ti ti-upload" />
          </button> */}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Roles</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Roles
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2 mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() => exportToCSV(dataforexport, "roles_data")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToExcel(dataforexport, "roles_data")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToPDF(dataforexport, "roles_data")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as PDF{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mb-2">
                <button
                  onClick={() => showDrawer("create")}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add New Roles
                </button>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Assets Lists */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Role List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedDataStatus
                      ? selectedDataStatus.charAt(0).toUpperCase() +
                      selectedDataStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedDataStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedDataStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedDataStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add Assets */}

      <Drawer
        title={modalType === "edit" ? "Edit Role" : "Add Role"}
        placement="right"
        width={400}
        onClose={handleCloseDrawer}
        visible={isDrawerVisible}
        style={{ marginTop: 50 }}
      >
        <Form layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={
              <>
                Role Name<span style={{ color: "red" }}> *</span>
              </>
            }

          >
            <Input
              className="role-input-box"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Enter role name"
            />
          </Form.Item>
          <Form.Item
            label={
              <>
                Status<span style={{ color: "red" }}> *</span>
              </>
            }
          >
            <Select
              className="role-input-box"
              value={selectedStatus || ""}
              onChange={handleStatusChange}
              placeholder="Select status"
            >
              {Array.isArray(status) && status.length > 0 ? (
                status?.map((item: any) => (
                  <Select.Option className="role-input-status" key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))
              ) : (
                <Select.Option value={""}>No status available</Select.Option>
              )}{" "}
            </Select>
          </Form.Item>

          <div className="modal-footer">
            <Button onClick={handleCloseDrawer} className="btn-light me-2">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={isLoading}>
              {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
              {modalType === "edit" ? "Update Role" : "Add Role"}
            </Button>
          </div>
        </Form>
      </Drawer>

      {/* /Add Assets */}

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.RoleId && handleDeleteRole(selectedRowData.RoleId)
        }
      />
      <style>
        {`
  .role-input-box {
    border: 1px solid #8c8c8c !important; 
    border-radius: 4px;
    background-color: #fff !important;     
    color: #000;
    box-shadow: none !important;
    outline: none !important;
  }
  .role-input-status {
    background-color: #fff !important;     
    color: #000;
    box-shadow: none !important;
    outline: none !important;
  }

  .role-input-box:focus,
  .role-input-box:focus-within {
    border-color: #8c8c8c !important;    
    box-shadow: none !important;           
    background-color: #fff !important;     
  }

  /* Optional: remove default focus outline for consistency */
  .role-input-box:focus-visible {
    outline: none !important;
  }
`}
      </style>



    </>
  );
};

export default RolesPermission;
